/**
 * `command.run`: the one intent through which every editor command (menus,
 * palette, dialogs) runs, for the user and for agents alike. The command's
 * own definition supplies footprint, validation, preparation and compilation.
 */
import type { ActorRef, CommandRunIntent, Intent } from '@maipo/agent-protocol';
import { editorCommands } from '../commands';
import type { EditorCommand } from '../commands/registry';
import { defineIntent, IntentRejection, policyFor } from '../registry';
import { error } from './common';

function commandOf(intent: CommandRunIntent): EditorCommand | undefined {
  return editorCommands().get(intent.command);
}

function params(intent: CommandRunIntent): Record<string, unknown> {
  return intent.params ?? {};
}

/** Fills a command's agent defaults into a `command.run` intent dispatched by an agent or add-on. */
export function withActorDefaults(intent: Intent, actor: ActorRef): Intent {
  if (intent.type !== 'command.run' || policyFor(actor).locks !== 'enforce') return intent;
  const defaults = commandOf(intent)?.agentDefaults;
  if (!defaults) return intent;
  return { ...intent, params: { ...defaults, ...params(intent) } };
}

export const commandRun = defineIntent<CommandRunIntent>({
  type: 'command.run',
  version: 1,
  footprint: (document, intent, aliases) => {
    const command = commandOf(intent);
    return command ? command.footprint(document, params(intent), aliases) : { reads: [], writes: [], structural: false, document: false };
  },
  validate: (document, intent, context) => {
    const command = commandOf(intent);
    if (!command) return [error('UNSUPPORTED_COMMAND', `Command ${JSON.stringify(intent.command).slice(0, 80)} does not exist.`)];
    return command.validate(document, params(intent), context);
  },
  prepare: async (session, intent, signal, context) => commandOf(intent)?.prepare?.(session, params(intent), signal, context),
  needsPrepare: (intent, document) => {
    const command = commandOf(intent);
    return command?.prepare !== undefined && (command.needsPrepare?.(params(intent), document) ?? true);
  },
  releasePrepared: (intent, prepared) => commandOf(intent)?.releasePrepared?.(params(intent), prepared),
  compile: (document, intent, prepared, aliases) => {
    const command = commandOf(intent);
    if (!command) throw new IntentRejection('UNSUPPORTED_COMMAND', `Command ${intent.command} does not exist.`);
    return command.compile(document, params(intent), prepared, aliases);
  },
  describe: (document, intent, aliases) => commandOf(intent)?.describe?.(document, params(intent), aliases) ?? [],
});
